import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { v7 as uuidv7 } from "uuid";
import SettingsForm from "./SettingsForm";
import Question from "../commons/model/Question";
import sfetch from "../commons/utils/super-fetch";
import InputState from "../commons/model/form/InputState";
import MaxDistanceInput from "./MaxDistanceInput";
import { Distance } from "../commons/model/address/Distance";
import SettingPracticeQuestionOverrideForm, {
  PracticeQuestionOverrideFormSettings,
} from "./SettingPracticeQuestionOverrideForm";
import SettingsVCQuestionsForm from "./SettingVCQuestionsForm";
import { AvailableEntitiesSettings } from "../commons/model/EntitySettings";
import PatientPracticeSelectionInput from "./PatientPracticeSelectionInput";
import PatientPracticeSelection from "../commons/model/PatientPracticeSelection";

export interface SettingsVirtualConsultationFormProps {
  virtualConsultationFormSettings: VirtualConsultationFormSettings;
  updateUrl: string;
}

interface SettingsVirtualConsultationFormState extends VirtualConsultationFormSettingsUpdate {
  errorMessage?: string;
}

interface VirtualConsultationFormSettings {
  editQuestions: boolean;
  questions?: Question[];
  practiceQuestionOverrideFormSettings?: PracticeQuestionOverrideFormSettings;
  practiceSearchFormSettings?: PracticeSearchFormSettings;
  patientPracticeSelection?: PatientPracticeSelection;
}

interface VirtualConsultationFormSettingsUpdate {
  questions?: Question[];
  practiceQuestionOverrideFormSettings?: PracticeQuestionOverrideFormSettings;
  practiceSearchFormSettings?: PracticeSearchFormSettings;
  patientCanSelectPractice: boolean;
}

interface PracticeSearchFormSettings {
  maxDistance?: Distance;
  defaultMaxDistance: Distance;
}

const SettingsVirtualConsultationForm = ({
  settingsVirtualConsultationForm,
  entities,
  isDsoEmployee,
}: {
  settingsVirtualConsultationForm: SettingsVirtualConsultationFormProps;
  entities: AvailableEntitiesSettings;
  isDsoEmployee: boolean;
}) => {
  const [questionsStates, setQuestionsStates] = useState(new Map<string, InputState>());
  const { virtualConsultationFormSettings } = settingsVirtualConsultationForm;

  const initialState = useRef<SettingsVirtualConsultationFormState>({
    questions: virtualConsultationFormSettings.questions
      ? [...virtualConsultationFormSettings.questions]
      : null,
    practiceQuestionOverrideFormSettings:
      virtualConsultationFormSettings.practiceQuestionOverrideFormSettings
        ? { ...virtualConsultationFormSettings.practiceQuestionOverrideFormSettings }
        : null,
    practiceSearchFormSettings: virtualConsultationFormSettings.practiceSearchFormSettings,
    patientCanSelectPractice:
      virtualConsultationFormSettings.patientPracticeSelection.patientCanSelectPractice,
  }).current;

  const [state, setState] = useState<SettingsVirtualConsultationFormState>(initialState);
  const {
    questions,
    practiceQuestionOverrideFormSettings,
    practiceSearchFormSettings,
    patientCanSelectPractice,
  } = state;
  const { displayPatientPracticeSelection } =
    virtualConsultationFormSettings.patientPracticeSelection;

  const addQuestion = () => {
    setState((prev) => ({
      ...prev,
      questions: [...(prev.questions || []), { uid: uuidv7(), value: "" }],
    }));
  };

  const updateQuestion = (index: number, value: string) => {
    setState((prev) => {
      const questions = [...(prev.questions || [])];
      questions[index] = { ...questions[index], value };
      return { ...prev, questions };
    });
  };

  const updatePracticeOverrideQuestions = (value: boolean) => {
    setState((prev) => ({
      ...prev,
      practiceQuestionOverrideFormSettings: {
        ...prev.practiceQuestionOverrideFormSettings,
        practiceQuestionOverride: value,
      },
    }));
  };

  const updateDisplayUserQuestionnaireStep = (value: boolean) => {
    setState((prev) => ({
      ...prev,
      practiceQuestionOverrideFormSettings: {
        ...prev.practiceQuestionOverrideFormSettings,
        displayUserQuestionnaireStep: value,
      },
    }));
  };

  const updateDistance = (value: Distance) => {
    setState((prev) => ({
      ...prev,
      practiceSearchFormSettings: { ...prev.practiceSearchFormSettings, maxDistance: value },
    }));
  };

  const updatePatientPracticeSelection = (value: boolean) => {
    setState((prev) => ({ ...prev, patientCanSelectPractice: value }));
  };

  const removeQuestion = (index: number) => {
    setState((prev) => {
      const questions = [...prev.questions];
      const [removed] = questions.splice(index, 1);
      setQuestionsStates((prevMap) => {
        const updated = new Map(prevMap);
        updated.delete(removed.uid);
        return updated;
      });
      return { ...prev, questions };
    });
  };

  const cancel = () => setState(initialState);

  const checkValidity = () => !Array.from(questionsStates.values()).some((it) => it.status.invalid);

  const updateQuestionsStates = (uid: string, inputState: InputState) => {
    setQuestionsStates((prev) => new Map(prev.set(uid, inputState)));
  };

  const hasChanged = useMemo(
    () =>
      Object.keys(state).some(
        (field) => JSON.stringify(state[field]) !== JSON.stringify(initialState[field])
      ),
    [state]
  );

  const save = async () => {
    await sfetch(settingsVirtualConsultationForm.updateUrl, {
      method: "PUT",
      body: JSON.stringify(state),
      headers: { "Content-Type": "application/json" },
    });
    if (
      initialState.practiceQuestionOverrideFormSettings?.practiceQuestionOverride !==
        state.practiceQuestionOverrideFormSettings.practiceQuestionOverride &&
      practiceQuestionOverrideFormSettings?.updatePracticeQuestionOverrideUrl
    ) {
      await callUpdatePracticeOverrideQuestionsApi(
        state.practiceQuestionOverrideFormSettings.practiceQuestionOverride
      );
    }

    if (
      initialState.practiceQuestionOverrideFormSettings?.displayUserQuestionnaireStep !==
        state.practiceQuestionOverrideFormSettings.displayUserQuestionnaireStep &&
      practiceQuestionOverrideFormSettings?.updateDisplayUserQuestionnaireStepUrl
    ) {
      await callUpdateDisplayUserQuestionnaireStepApi(
        state.practiceQuestionOverrideFormSettings.displayUserQuestionnaireStep
      );
    }

    return document.location.reload();
  };

  const callUpdatePracticeOverrideQuestionsApi = async (value: boolean) => {
    const body = {
      enabled: value,
      entitySettingsId: { value: entities.selectedEntity.entitySettingsId.value },
    };
    await sfetch(
      virtualConsultationFormSettings.practiceQuestionOverrideFormSettings
        .updatePracticeQuestionOverrideUrl,
      { method: "PUT", body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }
    )
      .then(() =>
        setState((prev) => ({
          ...prev,
          practiceQuestionOverrideFormSettings: {
            ...prev.practiceQuestionOverrideFormSettings,
            practiceQuestionOverride: value,
          },
          errorMessage: null,
        }))
      )
      .catch((e) => {
        setState((prev) => ({
          ...prev,
          errorMessage: prev.errorMessage || i18n["Global.Status.technical_error"],
        }));
        throw e;
      });
  };

  const callUpdateDisplayUserQuestionnaireStepApi = async (value: boolean) => {
    const body = {
      enabled: value,
      entitySettingsId: { value: entities.selectedEntity.entitySettingsId.value },
    };
    await sfetch(
      virtualConsultationFormSettings.practiceQuestionOverrideFormSettings
        .updateDisplayUserQuestionnaireStepUrl,
      { method: "PUT", body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }
    )
      .then(() =>
        setState((prev) => ({
          ...prev,
          practiceQuestionOverrideFormSettings: {
            ...prev.practiceQuestionOverrideFormSettings,
            displayUserQuestionnaireStep: value,
          },
          errorMessage: null,
        }))
      )
      .catch((e) => {
        setState((prev) => ({
          ...prev,
          errorMessage: prev.errorMessage || i18n["Global.Status.technical_error"],
        }));
        throw e;
      });
  };

  useEffect(() => {
    Array.from(questionsStates.values()).forEach((it) => {
      const relatedQuestion = state.questions?.find((question) => question.uid === it.name);
      if (relatedQuestion && relatedQuestion.value !== it.value) {
        it.setValue(relatedQuestion.value);
      }
    });
  }, [questionsStates]);

  const hasContentBelowPatientSelection =
    practiceSearchFormSettings ||
    isDsoEmployee ||
    practiceQuestionOverrideFormSettings ||
    questions;

  return (
    <SettingsForm
      title={i18n["Page.Settings.VirtualConsultation.Customization.Form.Title"]}
      hasChanges={hasChanged}
      isValid={checkValidity()}
      onCancel={() => cancel()}
      onSave={() => save()}
      id={"virtual-consultation-customisation-form-customisation"}
    >
      {displayPatientPracticeSelection && (
        <>
          <div className="no-label-field-groups">
            <h3 className="title">
              {
                i18n[
                  "Page.Settings.VirtualConsultation.Customization.Form.PatientPracticeSelection.Title"
                ]
              }
            </h3>
            <div className="input-group">
              <PatientPracticeSelectionInput
                patientCanSelectPractice={patientCanSelectPractice}
                onValueChange={updatePatientPracticeSelection}
              />
            </div>
          </div>
          <p className="setting-description">
            {
              i18n[
                "Page.Settings.VirtualConsultation.Customization.Form.PatientPracticeSelection.Description"
              ]
            }
          </p>
          {hasContentBelowPatientSelection && <hr />}
        </>
      )}
      {practiceSearchFormSettings && (
        <>
          <p className="setting-description">
            {
              i18n[
                "Page.Settings.VirtualConsultation.Customization.Form.CustomizePracticeResearchRadius.SubTitle"
              ]
            }
          </p>
          <div className="no-label-field-groups">
            <h3 className="title">
              {i18n["Page.Settings.VirtualConsultation.Customization.Form.MaximumDistance.Title"]}
            </h3>
            <div className="input-group">
              <MaxDistanceInput
                defaultDistance={practiceSearchFormSettings.defaultMaxDistance}
                value={practiceSearchFormSettings.maxDistance}
                onValueChange={updateDistance}
              />
            </div>
          </div>
          <hr />
        </>
      )}
      {practiceQuestionOverrideFormSettings && (
        <>
          <SettingPracticeQuestionOverrideForm
            practiceQuestionOverrideFormSettings={practiceQuestionOverrideFormSettings}
            onPracticeOverrideQuestionsChangeToggle={updatePracticeOverrideQuestions}
            onDisplayUserQuestionnaireStepChangeToggle={updateDisplayUserQuestionnaireStep}
          />
          {questions && (
            <SettingsVCQuestionsForm
              canEditQuestions={virtualConsultationFormSettings.editQuestions}
              questions={questions}
              updateInputStates={updateQuestionsStates}
              addQuestion={addQuestion}
              updateQuestion={updateQuestion}
              removeQuestion={removeQuestion}
            />
          )}
        </>
      )}
    </SettingsForm>
  );
};

export default SettingsVirtualConsultationForm;
